import { useContext } from 'react';
import { ThemeModeContext } from './ThemeModeProvider.jsx';

/**
 * useThemeMode - Hook to access theme mode state and controls
 *
 * Provides access to the current theme mode and functions to toggle or set it.
 * Must be used within a ThemeModeProvider.
 *
 * @returns {Object} Theme mode state and controls
 * @returns {string} mode - Current theme mode ('light' or 'retro')
 * @returns {boolean} isRetro - True if retro mode is active
 * @returns {boolean} isLight - True if light mode is active
 * @returns {boolean} isDark - Alias for isRetro (backwards compatibility)
 * @returns {Function} toggleMode - Toggle between light and retro modes
 * @returns {Function} setMode - Set mode directly (mode: 'light' | 'retro')
 *
 * @throws {Error} If used outside ThemeModeProvider
 *
 * @example
 * // Basic usage
 * import { useThemeMode } from '@vortx/shared-ui/theme-mode';
 *
 * function ThemeToggle() {
 *   const { mode, isRetro, toggleMode } = useThemeMode();
 *
 *   return (
 *     <button onClick={toggleMode}>
 *       Switch to {isRetro ? 'light' : 'retro'} mode
 *     </button>
 *   );
 * }
 *
 * @example
 * // Conditional styling based on theme
 * function StyledComponent() {
 *   const { isRetro } = useThemeMode();
 *
 *   return (
 *     <Box sx={{
 *       bgcolor: isRetro ? 'background.paper' : 'grey.50',
 *       color: isRetro ? 'text.primary' : 'text.secondary'
 *     }}>
 *       Content
 *     </Box>
 *   );
 * }
 *
 * @example
 * // Setting mode directly
 * function ThemeSelector() {
 *   const { mode, setMode } = useThemeMode();
 *
 *   return (
 *     <select value={mode} onChange={(e) => setMode(e.target.value)}>
 *       <option value="light">Light</option>
 *       <option value="retro">Retro</option>
 *     </select>
 *   );
 * }
 */
export function useThemeMode() {
    const context = useContext(ThemeModeContext);

    if (!context) {
        throw new Error('useThemeMode must be used within a ThemeModeProvider');
    }

    return context;
}
